import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY        = 'trustify_user_token';
const REFRESH_KEY      = 'trustify_refresh_token';
const API_BASE         = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:8000';
const INACTIVITY_MS    = 30 * 60 * 1000;   // 30 min → auto-logout
const WARNING_BEFORE   = 5  * 60 * 1000;   // show warning 5 min before logout

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export function AuthProvider({ children }) {
  const [user, setUser]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [sessionExpiring, setSessionExpiring] = useState(false);

  const warnTimerRef   = useRef(null);
  const logoutTimerRef = useRef(null);

  // Restore session on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    fetch(`${API_BASE}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setUser({ ...data, token });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');

    localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_KEY, data.refresh_token);
    }
    setUser({ ...data.user, token: data.access_token });
    return data.user;
  }, []);

  const register = useCallback(async (username, email, password) => {
    const res = await fetch(`${API_BASE}/api/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    return data;
  }, []);

  const clearInactivityTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    warnTimerRef.current   = null;
    logoutTimerRef.current = null;
  }, []);

  const logout = useCallback(async () => {
    clearInactivityTimers();
    const token        = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    // Tell Keycloak to revoke the session (best-effort)
    if (token) {
      fetch(`${API_BASE}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization:    `Bearer ${token}`,
          'X-Refresh-Token': refreshToken || '',
        },
      }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setSessionExpiring(false);
    setUser(null);
    window.location.replace('/');
  }, [clearInactivityTimers]);

  const deleteAccount = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) throw new Error('Not authenticated');
    const res = await fetch(`${API_BASE}/api/v1/auth/account`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Account deletion failed');
    }
    // Wipe local state and redirect to login
    clearInactivityTimers();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setSessionExpiring(false);
    setUser(null);
    window.location.replace('/');
  }, [clearInactivityTimers]);

  // ── Inactivity tracking ─────────────────────────────────────────────────────
  const scheduleInactivityTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    setSessionExpiring(false);

    // Warn 5 min before the hard-logout
    warnTimerRef.current = setTimeout(() => {
      setSessionExpiring(true);
    }, INACTIVITY_MS - WARNING_BEFORE);

    // Hard logout after 30 min of inactivity
    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_MS);
  }, [logout]);

  const extendSession = useCallback(() => {
    setSessionExpiring(false);
    scheduleInactivityTimers();
  }, [scheduleInactivityTimers]);

  // Start / restart timers whenever a user logs in; tear down on logout
  useEffect(() => {
    if (!user) {
      clearInactivityTimers();
      return;
    }

    scheduleInactivityTimers();

    // Throttle: only reschedule timers once per second regardless of how
    // many mousemove/scroll events fire (avoids 60fps setTimeout churn).
    let lastFired = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastFired < 1000) return;
      lastFired = now;
      setSessionExpiring(prev => {
        if (!prev) scheduleInactivityTimers();
        return prev;
      });
    };

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
      clearInactivityTimers();
    };
  }, [user, scheduleInactivityTimers, clearInactivityTimers]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, deleteAccount, sessionExpiring, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
